import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TermsAndConditionsModal from './TermsAndConditionsModal';
import Pagination from '../../common/Pagination';
import LoadingOverlay from '../../common/LoadingOverlay';

// Helper to display the IsType value as a readable label
const getTypeLabel = (isType) => {
  switch (Number(isType)) {
    case 1: return { label: 'Purchase', color: '#0f3460' };
    case 2: return { label: 'Sales',    color: '#11998e' };
    case 3: return { label: 'Both',     color: '#9b59b6' };
    default: return { label: 'Unknown', color: '#999' };
  }
};

// Helper to display IsInternational as Local / International
const getScopeLabel = (isInternational) => {
  if (isInternational === true || isInternational === 1) {
    return { label: 'International', color: '#e67e22' };
  }
  return { label: 'Local', color: '#0f3460' };
};

function TermsAndConditionsIndex() {
  useSessionValidation();
  const { canCreateMaster, canEditMaster, canDeleteMaster } = usePermissions();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, logout, token } = useAuth();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTerms, setSelectedTerms] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return;
    setLoading(true);
    fetchTableData().finally(() => setLoading(false));
  }, [companyId, token]);

  const fetchTableData = async () => {
    try {
      const response = await fetchData('TermsAndConditionApi', `/${companyId}`, token);
      console.log('Terms & Conditions data response:', response);
      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Terms & Conditions:', error);
      setData([]);
    }
  };

  const handleDelete = async () => {
    deleteData('TermsAndConditionApi', `${deleteItemId}`, token)
      .then(() => {
        fetchTableData();
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
      })
      .catch((error) => console.error('Error deleting record:', error));
  };

  const handleActivate = (itemId) => {
    activateData('TermsAndConditionApi', `${itemId}`, token)
      .then(() => fetchTableData())
      .catch((error) => console.error('Error activating record:', error));
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  const handleOpenModal = (mode, terms = null) => {
    setModalMode(mode);
    setSelectedTerms(terms);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTerms(null);
    window.location.reload();
  };

  const handleSaveTerms = () => {
    fetchTableData();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const typeLabel = getTypeLabel(item.IsType).label.toLowerCase();
    const scopeLabel = getScopeLabel(item.IsInternational).label.toLowerCase();
    return (
      item.Name?.toLowerCase().includes(searchLower) ||
      typeLabel.includes(searchLower) ||
      scopeLabel.includes(searchLower)
    );
  });

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="View Details" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('view', item)}
          style={{
            minWidth: '22px', height: '22px', borderRadius: '6px',
            color: 'white', padding: '2px',
            boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
            border: 'none', fontSize: '10px', fontWeight: '600',
          }}
        >
          👁️
        </Button>
      </Tooltip>

      {canEditMaster && (
        <Tooltip title="Edit Terms & Conditions" arrow>
          <Button
            size="small"
            onClick={() => handleOpenModal('edit', item)}
            style={{
              minWidth: '22px', height: '22px', borderRadius: '6px',
              color: 'white', padding: '2px',
              boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
              border: 'none', fontSize: '10px', fontWeight: '600',
            }}
          >
            ✏️
          </Button>
        </Tooltip>
      )}

      {canDeleteMaster && (
        <Tooltip title={item.IsActive ? 'Delete' : 'Activate'} arrow>
          <Button
            size="small"
            onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}
            style={{
              minWidth: '22px', height: '22px', borderRadius: '6px',
              color: 'white', padding: '2px',
              boxShadow: item.IsActive
                ? '0 1px 4px rgba(255, 107, 107, 0.3)'
                : '0 1px 4px rgba(78, 205, 196, 0.3)',
              border: 'none', fontSize: '10px', fontWeight: '600',
            }}
          >
            {item.IsActive ? '🗑️' : '✅'}
          </Button>
        </Tooltip>
      )}
    </div>
  );

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .action-button { text-transform: none; font-weight: 600; letter-spacing: 0.5px; }
        `}</style>

        {loading && <LoadingOverlay message="Loading..." />}

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px', fontWeight: '700', margin: 0,
                  letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase',
                }}>
                  <span style={{ color: 'black' }}>Terms &amp; Conditions</span>
                </h4>
              </div>
            </div>
          </div>

          {/* Search + Create Button */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by text or type..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#0f3460', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px', backgroundColor: '#f8f9fa',
                  fontSize: '13px', height: '29px',
                  '&:hover fieldset':   { borderColor: '#0f3460' },
                  '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                },
              }}
            />
            {canCreateMaster && (
              <Tooltip title="Create new terms & conditions" arrow>
                <Button
                  variant="contained"
                  onClick={() => handleOpenModal('create')}
                  className="action-button"
                  style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    color: 'white', padding: '4px 12px', borderRadius: '6px',
                    fontSize: '12px', fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                    border: 'none', minHeight: '29px', textTransform: 'none',
                    letterSpacing: '0.5px', display: 'flex', alignItems: 'center',
                    gap: '4px', whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ fontSize: '12px' }}>+</span>
                  Create Terms
                </Button>
              </Tooltip>
            )}
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px', overflow: 'hidden',
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="terms and conditions table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    height: '27px',
                  }}>
                    {[
                      { header: '#',    align: 'left'  },
                      { header: 'Type', align: 'left'  },
                      { header: 'Scope', align: 'left' },
                      { header: 'Terms & Conditions Text', align: 'left' },
                      { header: 'Actions', align: 'right' },
                    ].map((col) => (
                      <TableCell key={col.header} style={{
                        color: 'white', fontWeight: '600', fontSize: '11px',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        padding: '4px 12px', textAlign: col.align, height: '27px',
                      }}>
                        {col.header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        style={{ textAlign: 'center', fontSize: '13px', color: '#999', padding: '20px' }}
                      >
                        No records found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((item, index) => {
                      const typeInfo = getTypeLabel(item.IsType);
                      const scopeInfo = getScopeLabel(item.IsInternational);
                      return (
                        <TableRow key={item.Id} style={{
                          background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          borderBottom: '2px solid #f0f0f0',
                        }}>
                          {/* Row number */}
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', width: '40px' }}>
                            {startIndex + index + 1}
                          </TableCell>
                          {/* Type chip */}
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', width: '100px' }}>
                            <Chip
                              label={typeInfo.label}
                              size="small"
                              sx={{
                                backgroundColor: `${typeInfo.color} !important`,
                                color: 'white !important',
                                fontSize: '10px',
                                height: '20px',
                                fontWeight: '600',
                                '& .MuiChip-label': {
                                  color: 'white',
                                  fontWeight: '600',
                                  fontSize: '10px',
                                },
                              }}
                            />
                          </TableCell>
                          {/* Scope chip */}
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', width: '120px' }}>
                            <Chip
                              label={scopeInfo.label}
                              size="small"
                              sx={{
                                backgroundColor: `${scopeInfo.color} !important`,
                                color: 'white !important',
                                fontSize: '10px',
                                height: '20px',
                                fontWeight: '600',
                                '& .MuiChip-label': {
                                  color: 'white',
                                  fontWeight: '600',
                                  fontSize: '10px',
                                },
                              }}
                            />
                          </TableCell>
                          {/* Terms text (truncated for table view) */}
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                            <span title={item.Name} style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: '1.4',
                            }}>
                              {item.Name}
                            </span>
                          </TableCell>
                          {/* Actions */}
                          <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                            {renderActions(item)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
        >
          <DialogTitle id="alert-dialog-title" style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{ fontSize: '13px', color: '#666' }}>
              Are you sure you want to delete this Terms &amp; Conditions record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff', borderRadius: '6px', padding: '4px 8px',
                fontSize: '12px', fontWeight: '600', textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)', border: 'none',
                display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px',
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span> Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff', borderRadius: '6px', padding: '4px 8px',
                fontSize: '12px', fontWeight: '600', textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)', border: 'none',
                display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px',
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span> Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Terms & Conditions Modal */}
        <TermsAndConditionsModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveTerms}
          termsData={selectedTerms}
          mode={modalMode}
          existingTerms={data}
        />
      </div>
    </Fade>
  );
}

export default TermsAndConditionsIndex;
